import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useForm,
  usePublishForm,
  useSaveDraft,
  useUpdateForm,
} from '../../api/forms.api';
import { checkPublishReadiness, useBuilderStore } from '../../features/forms/builder/store';
import { FormRenderer } from '../../features/forms/renderer/FormRenderer';
import { resolveBrand } from '../../features/forms/renderer/brand';
import type { FormConsentSnapshot, FormFieldDefinition } from '@engage/types';
import { FieldPalette } from '../../features/forms/builder/FieldPalette';
import { FieldSettings } from '../../features/forms/builder/FieldSettings';
import { FormSettings } from '../../features/forms/builder/FormSettings';
import { PublishModal } from '../../features/forms/builder/PublishModal';
import { SubmissionsModal } from '../../features/forms/builder/SubmissionsModal';
import { hostedFormUrl } from '../../features/forms/hostedUrl';
import { Btn, Icon, ICONS, PathChip, StatusChip } from '../../features/forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../features/forms/ui/tokens';

const AUTOSAVE_DEBOUNCE_MS = 800;

type PreviewMode = 'empty' | 'prefill';
type SettingsTab = 'form' | 'field';

export function FormBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useForm(id);
  const state = useBuilderStore();
  const saveDraft = useSaveDraft(id ?? '');
  const updateForm = useUpdateForm(id ?? '');
  const publish = usePublishForm(id ?? '');
  const [savingState, setSavingState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [submissionsOpen, setSubmissionsOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('empty');
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('form');

  useEffect(() => {
    if (!data) return;
    state.initFrom(data.form, data.current_version?.fields ?? []);
  }, [data?.form.id]);

  useEffect(() => {
    // When the store gains a selected field via any path (addField,
    // re-init from server, etc.), flip the right pane to the Field tab.
    // The explicit click-handler `pickField` below also calls this so that
    // re-clicking an already-selected field — which leaves the store value
    // unchanged — still flips the tab.
    if (state.selectedFieldKey) setSettingsTab('field');
  }, [state.selectedFieldKey]);

  const pickField = (key: string) => {
    state.selectField(key);
    setSettingsTab('field');
  };

  useEffect(() => {
    if (!state.dirty || !id) return;
    const timer = setTimeout(async () => {
      setSavingState('saving');
      try {
        await Promise.all([
          updateForm.mutateAsync({
            name: state.name,
            slug: state.slug,
            brand_override: state.brandOverride,
            consent_mode: state.consentMode,
            consent_text: state.consentText,
            gated_document_id: state.gatedDocumentId,
            gated_delivery_mode: state.gatedDeliveryMode,
            gated_document_ttl_seconds: state.gatedDocumentTtlSeconds,
            gated_email_subject: state.gatedEmailSubject,
          }),
          saveDraft.mutateAsync({ fields: state.fields }),
        ]);
        state.markSaved();
        setSavedAt(new Date());
        setSavingState('saved');
      } catch {
        setSavingState('idle');
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [
    state.dirty,
    state.fields,
    state.name,
    state.slug,
    state.brandOverride,
    state.consentMode,
    state.consentText,
    state.gatedDocumentId,
    state.gatedDeliveryMode,
    state.gatedDocumentTtlSeconds,
    state.gatedEmailSubject,
  ]);

  const resolvedBrand = useMemo(
    () => resolveBrand(null, state.brandOverride),
    [state.brandOverride]
  );
  const consentSnapshot: FormConsentSnapshot | null = useMemo(() => {
    if (!state.consentMode || state.consentMode === 'DISABLED') return null;
    return { mode: state.consentMode, text: state.consentText ?? '' };
  }, [state.consentMode, state.consentText]);

  if (isLoading || !data) {
    return (
      <div
        style={{ padding: 40, fontFamily: FORM_UI_FONT, color: C.sub, textAlign: 'center' }}
      >
        Loading…
      </div>
    );
  }

  const issues = checkPublishReadiness(state);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 53px)',
        fontFamily: FORM_UI_FONT,
        color: C.ink,
        background: C.bg,
      }}
    >
      <TopBar
        formName={state.name}
        onNameChange={state.setName}
        pathType={state.pathType}
        status={data.form.status}
        savingState={savingState}
        savedAt={savedAt}
        publishedSlug={data.form.current_published_version_id ? data.form.slug : null}
        onBack={() => navigate('/forms')}
        onPublishClick={() => setPublishOpen(true)}
        onSubmissionsClick={() => setSubmissionsOpen(true)}
      />

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* LEFT — palette */}
        <aside
          style={{
            width: 224,
            background: '#fff',
            borderRight: `1px solid ${C.line}`,
            overflowY: 'auto',
          }}
        >
          <FieldPalette
            pathType={state.pathType}
            onAdd={state.addField}
            onAddPreset={state.addPresetField}
            existingKeys={state.fields.map((f) => f.key)}
          />
        </aside>

        {/* CENTRE — live preview */}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            padding: '20px 26px',
            background: C.bg,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 14,
            }}
          >
            <div style={{ fontSize: 12.5, fontWeight: 600, color: C.indigoSoft }}>
              Live preview
            </div>
            <div
              style={{
                display: 'flex',
                gap: 4,
                background: '#fff',
                borderRadius: 9,
                padding: 3,
                border: `1px solid ${C.line}`,
              }}
            >
              <PreviewToggle
                value={previewMode}
                onChange={setPreviewMode}
                options={[
                  { value: 'empty', label: 'View as empty' },
                  { value: 'prefill', label: 'Pre-filled (sample)' },
                ]}
              />
            </div>
          </div>

          {state.fields.length === 0 ? (
            <EmptyPreview />
          ) : (
            <>
              <div style={{ maxWidth: 520, margin: '0 auto' }}>
                <FormRenderer
                  fields={state.fields}
                  brand={resolvedBrand}
                  consent={consentSnapshot}
                  mode="preview"
                  values={previewMode === 'prefill' ? samplePrefill(state.fields) : undefined}
                  selectedFieldKey={state.selectedFieldKey}
                  onFieldFocus={pickField}
                />
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: C.faint,
                  marginTop: 12,
                  textAlign: 'center',
                  maxWidth: 520,
                  marginInline: 'auto',
                }}
              >
                Rendered by FormRenderer with the resolved BrandKit · the same component powers
                the hosted page and the iframe embed
              </div>
            </>
          )}
        </main>

        {/* RIGHT — settings */}
        <aside
          style={{
            width: 340,
            background: '#fff',
            borderLeft: `1px solid ${C.line}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', borderBottom: `1px solid ${C.line}` }}>
            <TabButton
              label="Form"
              active={settingsTab === 'form'}
              onClick={() => setSettingsTab('form')}
            />
            <TabButton
              label="Field"
              active={settingsTab === 'field'}
              onClick={() => state.selectedFieldKey && setSettingsTab('field')}
              disabled={!state.selectedFieldKey}
              hint={!state.selectedFieldKey ? '· select a field' : undefined}
            />
          </div>
          <div style={{ overflowY: 'auto', padding: '18px 18px 30px', flex: 1 }}>
            {settingsTab === 'field' && state.selectedFieldKey ? (
              <FieldSettings
                field={findField(state.fields, state.selectedFieldKey)}
                pathType={state.pathType}
                earlierFields={earlierFields(state.fields, state.selectedFieldKey)}
                onChange={(patch) => state.updateField(state.selectedFieldKey!, patch)}
                onRemove={() => state.removeField(state.selectedFieldKey!)}
                onClose={() => {
                  state.selectField(null);
                  setSettingsTab('form');
                }}
              />
            ) : (
              <FormSettings />
            )}
          </div>
        </aside>
      </div>

      {submissionsOpen && (
        <SubmissionsModal
          formId={data.form.id}
          fields={state.fields}
          onClose={() => setSubmissionsOpen(false)}
        />
      )}

      {publishOpen && (
        <PublishModal
          issues={issues}
          isPublishing={publish.isPending}
          nextVersionNumber={(data.published_version?.version_number ?? 0) + 1}
          slug={state.slug}
          onClose={() => setPublishOpen(false)}
          // The modal flips to a success view on resolve (showing the hosted
          // URL + embed snippet) — so we DON'T close it here. The confirmation
          // is the prompt the user asked for: previously the screen just
          // closed with no feedback.
          onPublish={async () => {
            await publish.mutateAsync();
          }}
        />
      )}
    </div>
  );
}

// ─── topbar ──────────────────────────────────────────────────────────────────

function TopBar(props: {
  formName: string;
  onNameChange: (v: string) => void;
  pathType: 'CANDIDATE' | 'CONTACT';
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  savingState: 'idle' | 'saving' | 'saved';
  savedAt: Date | null;
  publishedSlug: string | null;
  onBack: () => void;
  onPublishClick: () => void;
  onSubmissionsClick: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 24px',
        background: '#fff',
        borderBottom: `1px solid ${C.line}`,
      }}
    >
      <button
        type="button"
        onClick={props.onBack}
        style={{
          fontSize: 12,
          color: C.faint,
          fontWeight: 500,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: FORM_UI_FONT,
          padding: 0,
        }}
      >
        Engage › Forms ›
      </button>
      <input
        value={props.formName}
        onChange={(e) => props.onNameChange(e.target.value)}
        style={{
          fontFamily: FORM_UI_FONT,
          fontWeight: 700,
          fontSize: 17,
          color: C.indigo,
          border: '1px solid transparent',
          borderRadius: 7,
          padding: '4px 8px',
          width: 320,
          background: 'transparent',
          outline: 'none',
        }}
      />
      <PathChip type={props.pathType} size="lg" />
      <StatusChip status={props.status} />
      <SaveStateBadge state={props.savingState} savedAt={props.savedAt} />
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
        <Btn kind="ghost" onClick={props.onSubmissionsClick}>
          <Icon d={ICONS.layers} size={15} color={C.ink} />
          Submissions
        </Btn>
        <Btn
          kind="ghost"
          disabled={!props.publishedSlug}
          onClick={() => {
            if (props.publishedSlug) window.open(hostedFormUrl(props.publishedSlug), '_blank');
          }}
        >
          <Icon d={ICONS.eye} size={15} color={C.ink} />
          Preview
        </Btn>
        <Btn kind="primary" onClick={props.onPublishClick}>
          Publish
        </Btn>
      </div>
    </div>
  );
}

function SaveStateBadge(props: {
  state: 'idle' | 'saving' | 'saved';
  savedAt: Date | null;
}) {
  if (props.state === 'idle') return null;
  const label =
    props.state === 'saving'
      ? 'Saving…'
      : props.savedAt
        ? `Saved ${formatTime(props.savedAt)} · auto-saving`
        : 'Saved · auto-saving';
  return (
    <span
      style={{
        fontSize: 11.5,
        color: C.faint,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 99,
          background: props.state === 'saving' ? C.warnDot : C.teal,
        }}
      />
      {label}
    </span>
  );
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function TabButton(props: {
  label: string;
  active: boolean;
  disabled?: boolean;
  hint?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.disabled ? undefined : props.onClick}
      disabled={props.disabled}
      style={{
        flex: 1,
        fontFamily: FORM_UI_FONT,
        fontWeight: 600,
        fontSize: 13,
        cursor: props.disabled ? 'default' : 'pointer',
        padding: '13px 0',
        border: 'none',
        background: 'transparent',
        color: props.active ? C.indigo : props.disabled ? C.faint : C.sub,
        borderBottom: `2px solid ${props.active ? C.indigo : 'transparent'}`,
      }}
    >
      {props.label}
      {props.hint && <span style={{ fontSize: 10, marginLeft: 4 }}> {props.hint}</span>}
    </button>
  );
}

function PreviewToggle<T extends string>(props: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <>
      {props.options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => props.onChange(o.value)}
          style={{
            fontFamily: FORM_UI_FONT,
            fontSize: 11.5,
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            borderRadius: 7,
            padding: '6px 11px',
            background: props.value === o.value ? C.indigo : 'transparent',
            color: props.value === o.value ? '#fff' : C.sub,
          }}
        >
          {o.label}
        </button>
      ))}
    </>
  );
}

function EmptyPreview() {
  return (
    <div
      style={{
        maxWidth: 520,
        margin: '40px auto',
        background: '#fff',
        borderRadius: 16,
        border: `2px dashed ${C.line}`,
        padding: '60px 32px',
        textAlign: 'center',
        color: C.sub,
      }}
    >
      <Icon d={ICONS.forms} size={36} color={C.faint} />
      <div style={{ fontSize: 14, fontWeight: 600, color: C.indigoSoft, marginTop: 14 }}>
        Add fields from the palette to begin
      </div>
      <div style={{ fontSize: 12, color: C.faint, marginTop: 6 }}>
        Your live preview will appear here.
      </div>
    </div>
  );
}

function findField(fields: FormFieldDefinition[], key: string): FormFieldDefinition {
  const f = fields.find((x) => x.key === key);
  if (!f) throw new Error(`Selected field not found: ${key}`);
  return f;
}

function earlierFields(
  fields: FormFieldDefinition[],
  key: string
): FormFieldDefinition[] {
  const idx = fields.findIndex((x) => x.key === key);
  return idx === -1 ? [] : fields.slice(0, idx);
}

/** Sample values for the "Pre-filled (sample)" toggle in the builder preview.
 *  Field-key aware — populates known JT candidate keys with believable values. */
function samplePrefill(fields: FormFieldDefinition[]): Record<string, unknown> {
  const samples: Record<string, unknown> = {
    first_name: 'Marcus',
    last_name: 'Iverson',
    email: 'marcus.i@example.com',
    phone: '+61 412 555 308',
    title: 'Senior DevOps Engineer',
    company: 'Bridge Talent',
    location: 'Sydney, NSW',
    availability_status: 'AVAILABLE_NOW',
    notice_period: '4 weeks',
    salary_min: 185000,
    salary_max: 215000,
  };
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.key in samples) out[f.key] = samples[f.key];
  }
  return out;
}
